import { t } from "../i18n"
import { Controller } from "@hotwired/stimulus"
import {
  createEditor,
  $getRoot,
  $getSelection,
  $isRangeSelection,
  $createParagraphNode,
  $createTextNode,
  FORMAT_TEXT_COMMAND,
  UNDO_COMMAND,
  REDO_COMMAND,
  SELECTION_CHANGE_COMMAND,
  COMMAND_PRIORITY_LOW,
} from "lexical"
import { $generateHtmlFromNodes, $generateNodesFromDOM } from "@lexical/html"
import { registerRichText, HeadingNode, QuoteNode } from "@lexical/rich-text"
import {
  ListNode,
  ListItemNode,
  INSERT_UNORDERED_LIST_COMMAND,
  INSERT_ORDERED_LIST_COMMAND,
  registerList,
} from "@lexical/list"
import { registerHistory, createEmptyHistoryState } from "@lexical/history"
import { mergeRegister } from "@lexical/utils"
import { LinkNode, AutoLinkNode, registerLink, registerAutoLink, createLinkMatcherWithRegExp } from "@lexical/link"

const URL_REGEX = /((https?:\/\/(www\.)?)|(www\.))[-a-zA-Z0-9@:%._+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_+.~#?&//=]*)/

// Connects to data-controller="lexical-editor"
// Replaces Trix editor throughout the app.
//
// Required targets in the partial:
//   data-lexical-editor-target="toolbar"         – toolbar button row
//   data-lexical-editor-target="editorContainer" – contenteditable area
//   data-lexical-editor-target="hiddenInput"     – <textarea hidden> that holds HTML for form submission
//
// Public API (callable by other controllers via Stimulus getControllerForElementAndIdentifier):
//   loadHTML(html)   – replace editor content
//   getHTML()        – returns current HTML string
//   insertText(text) – insert text at cursor (voice dictation)
//   focus()          – focus the editor
//   getEditorElement() – returns the contenteditable div (for @mentions)

export default class extends Controller {
  static targets = ["toolbar", "editorContainer", "hiddenInput"]

  connect() {
    this._initEditor()
    this._loadInitialContent()
    this._registerAll()
    this._registerFormSync()

    // Notify other controllers (e.g. mentions) that this editor is ready
    this.element.dispatchEvent(
      new CustomEvent("lexical:initialize", { bubbles: true, detail: { controller: this } })
    )
    // Also fire on document so globally-listening controllers can pick it up
    document.dispatchEvent(
      new CustomEvent("lexical:initialize", { bubbles: true, detail: { controller: this, element: this.element } })
    )
  }

  disconnect() {
    if (this._cleanup) this._cleanup()
    if (this._formSubmitHandler && this._form) {
      this._form.removeEventListener("submit", this._formSubmitHandler)
    }
    if (this.editor) this.editor.setRootElement(null)
  }

  // ── Initialisation ────────────────────────────────────────────────

  _initEditor() {
    const uid = this.element.id || Math.random().toString(36).slice(2)
    this.editor = createEditor({
      namespace: `LexicalEditor_${uid}`,
      nodes: [HeadingNode, QuoteNode, ListNode, ListItemNode, LinkNode, AutoLinkNode],
      theme: {
        text: {
          bold:          "lexical-bold",
          italic:        "lexical-italic",
          underline:     "lexical-underline",
          strikethrough: "lexical-strikethrough",
        },
        list: {
          ul:       "lexical-ul",
          ol:       "lexical-ol",
          listitem: "lexical-listitem",
          nested:   { listitem: "lexical-nested-listitem" },
        },
        paragraph: "lexical-paragraph",
        link: "lexical-link",
      },
      onError(error) { console.error("[lexical-editor]", error) },
    })
    this.editor.setRootElement(this.editorContainerTarget)
  }

  _loadInitialContent() {
    const html = this.hiddenInputTarget.value?.trim()
    this.editor.update(() => {
      const root = $getRoot()
      root.clear()

      this._appendContentNodes(root, html)
    }, { onUpdate: () => this._updatePlaceholder() })
  }

  _registerAll() {
    const registrations = []

    // Core sync MUST always register so form submission works reliably.
    registrations.push(
      this.editor.registerUpdateListener(({ editorState }) => {
        editorState.read(() => {
          this.hiddenInputTarget.value = $generateHtmlFromNodes(this.editor, null)
        })
        this._updateToolbarState()
        this._updatePlaceholder()
      })
    )

    registrations.push(
      this.editor.registerCommand(
        SELECTION_CHANGE_COMMAND,
        () => { this._updateToolbarState(); return false },
        COMMAND_PRIORITY_LOW
      )
    )

    const safeRegister = (name, fn) => {
      try {
        registrations.push(fn())
      } catch (error) {
        console.error(`[lexical-editor] ${name} disabled due to registration error`, error)
      }
    }

    safeRegister("rich text", () => registerRichText(this.editor))
    safeRegister("list", () => registerList(this.editor))
    safeRegister("history", () => registerHistory(this.editor, createEmptyHistoryState(), 300))
    safeRegister("link", () => registerLink(this.editor))
    safeRegister("autolink", () =>
      registerAutoLink(this.editor, {
        matchers: [
          createLinkMatcherWithRegExp(URL_REGEX, (text) => {
            return text.startsWith("http") ? text : `https://${text}`
          })
        ],
        changeHandlers: [],
        excludeParents: []
      })
    )

    this._cleanup = mergeRegister(...registrations.filter(Boolean))
    // Set initial placeholder state
    this._updatePlaceholder()
  }

  _registerFormSync() {
    this._form = this.element.closest("form")
    if (!this._form) return

    this._formSubmitHandler = () => {
      this.hiddenInputTarget.value = this.getHTML()
    }
    this._form.addEventListener("submit", this._formSubmitHandler)
  }

  // ── Toolbar state ─────────────────────────────────────────────────

  _updateToolbarState() {
    if (!this.hasToolbarTarget) return
    requestAnimationFrame(() => {
      this.editor.getEditorState().read(() => {
        const sel = $getSelection()
        ;["bold", "italic", "underline"].forEach(fmt => {
          const btn = this.toolbarTarget.querySelector(`[data-format="${fmt}"]`)
          if (!btn) return
          const active = $isRangeSelection(sel) && sel.hasFormat(fmt)
          btn.setAttribute("aria-pressed", String(active))
          btn.classList.toggle("lexical-toolbar-active", active)
        })
      })
    })
  }

  _updatePlaceholder() {
    let isEmpty = false
    this.editor.getEditorState().read(() => {
      const root = $getRoot()
      const text = root.getTextContent()
      const children = root.getChildren()
      isEmpty = text.trim() === "" && children.length <= 1
    })
    this.editorContainerTarget.dataset.empty = String(isEmpty)
  }

  // ── Public API (for other Stimulus controllers) ───────────────────

  /** Replace the entire editor content with the given HTML string */
  loadHTML(html) {
    if (!this.editor) return
    this.editor.update(() => {
      const root = $getRoot()
      root.clear()

      this._appendContentNodes(root, html)
    })
  }

  _appendContentNodes(root, html) {
    if (!html) {
      root.append($createParagraphNode())
      return
    }

    try {
      const parser = new DOMParser()
      const dom = parser.parseFromString(html, "text/html")
      const nodes = $generateNodesFromDOM(this.editor, dom)

      if (nodes.length > 0) {
        root.append(...nodes)
        return
      }

      const text = dom.body?.textContent?.trim()
      if (text) {
        const paragraph = $createParagraphNode()
        paragraph.append($createTextNode(text))
        root.append(paragraph)
      } else {
        root.append($createParagraphNode())
      }
    } catch (error) {
      console.error("[lexical-editor] Failed to load HTML content", error)
      const paragraph = $createParagraphNode()
      paragraph.append($createTextNode(String(html)))
      root.append(paragraph)
    }
  }

  /** Return the current content as an HTML string */
  getHTML() {
    let html = ""
    this.editor?.getEditorState().read(() => {
      html = $generateHtmlFromNodes(this.editor, null)
    })
    return html
  }

  /** Insert plain text at the current cursor position (used by voice dictation) */
  insertText(text) {
    if (!this.editor || !text) return
    this.editor.update(() => {
      const sel = $getSelection()
      if ($isRangeSelection(sel)) {
        sel.insertText(text + " ")
      } else {
        const root = $getRoot()
        const last = root.getLastChild()
        if (last) {
          try {
            const textNode = $createTextNode(text + " ")
            last.append(textNode)
          } catch {
            const para = $createParagraphNode()
            para.append($createTextNode(text + " "))
            root.append(para)
          }
        }
      }
    })
    this.editor.focus()
  }

  focus() { this.editor?.focus() }

  /** Returns the contenteditable div — used by mentions (Tribute.js) */
  getEditorElement() { return this.editorContainerTarget }

  // ── Toolbar actions (bound via data-action in the partial) ────────

  bold(event) {
    event.preventDefault()
    this.editor.dispatchCommand(FORMAT_TEXT_COMMAND, "bold")
    this.editor.focus()
  }

  italic(event) {
    event.preventDefault()
    this.editor.dispatchCommand(FORMAT_TEXT_COMMAND, "italic")
    this.editor.focus()
  }

  underline(event) {
    event.preventDefault()
    this.editor.dispatchCommand(FORMAT_TEXT_COMMAND, "underline")
    this.editor.focus()
  }

  bulletList(event) {
    event.preventDefault()
    this.editor.dispatchCommand(INSERT_UNORDERED_LIST_COMMAND, undefined)
    this.editor.focus()
  }

  numberedList(event) {
    event.preventDefault()
    this.editor.dispatchCommand(INSERT_ORDERED_LIST_COMMAND, undefined)
    this.editor.focus()
  }

  undo(event) {
    event.preventDefault()
    this.editor.dispatchCommand(UNDO_COMMAND, undefined)
    this.editor.focus()
  }

  redo(event) {
    event.preventDefault()
    this.editor.dispatchCommand(REDO_COMMAND, undefined)
    this.editor.focus()
  }
}
